import { useEffect } from 'react';
import Lenis from 'lenis';

// Module-scope ref pour permettre à scrollToHash() + le polling de __root.tsx
// d'accéder à l'instance Lenis active (Phase 27.46 — port pattern Buteau).
let lenisInstance: Lenis | null = null;
export function getLenis(): Lenis | null {
  return lenisInstance;
}

/**
 * scrollToHash — helper partagé. Utilisé par le polling cross-page nav
 * (__root.tsx) ET par les composants qui scrollent programmatiquement
 * (ExitIntentPopup, futures CTAs).
 *
 * Comportement : compute la targetY avec offset scroll-padding-top CSS,
 * délègue à Lenis si actif (desktop sans reduced-motion), sinon
 * scrollIntoView natif (mobile / a11y opt-out). Met aussi à jour l'URL
 * via history.replaceState.
 */
export function scrollToHash(id: string): void {
  if (typeof document === 'undefined') return;
  const el = document.getElementById(id);
  if (!el) return;

  if (lenisInstance) {
    // Desktop : Lenis. Compute offsetTop cumulatif + scroll-padding offset.
    const scrollPadding = parseFloat(
      getComputedStyle(document.documentElement).scrollPaddingTop || '80'
    );
    let top = 0;
    let current: HTMLElement | null = el;
    while (current) {
      top += current.offsetTop;
      current = current.offsetParent as HTMLElement | null;
    }
    const targetY = top - (Number.isFinite(scrollPadding) ? scrollPadding : 80);
    // Phase 27.78 — duration 1.1 → 0.9 pour cohérence avec SectionRail click
    // (0.6s) tout en gardant un peu plus de smooth pour les nav cross-page
    // (le polling tick s'occupe de re-scroller si layout change post-lazy).
    lenisInstance.scrollTo(targetY, { duration: 0.9 });
  } else {
    // Mobile / a11y : scrollIntoView natif. scroll-padding-top CSS gère
    // l'offset navbar auto.
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  window.history.replaceState(null, '', `#${id}`);
}

/**
 * useLenis — butter-smooth scroll via Lenis (~5KB).
 *
 * - Replaces native browser scroll with inertia-based smooth scrolling.
 * - Auto-disabled on touch (mobile keeps native scroll for performance + UX).
 * - Auto-disabled on prefers-reduced-motion (a11y).
 * - Plays nicely with anchor links (#section) via lenis.scrollTo.
 *
 * Mount once at app root via useLenis() hook in main.tsx or RootLayout.
 */
export function useLenis() {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    // Skip on touch devices — native momentum scroll is better UX on mobile
    if (window.matchMedia('(pointer: coarse)').matches) return;
    // Respect a11y opt-out
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    // Phase 27.44 perf — duration 1.2 → 0.8 + wheelMultiplier 1 → 0.85.
    // Settling plus court = moins de frames de lerp interpolation par scroll
    // tick = moins de getBoundingClientRect/onContentResize reads = moins
    // de forced reflow (perf trace 2026-05-26 : 162ms ForcedReflow tracé).
    // Le smooth scroll reste smooth, juste légèrement plus snappy. Easing
    // expo conservé pour le ressenti "luxe" sur les derniers ms du settle.
    const lenis = new Lenis({
      duration: 0.8,
      easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      wheelMultiplier: 0.85,
      touchMultiplier: 2,
      smoothWheel: true,
      syncTouch: false
    });
    lenisInstance = lenis;

    let rafId = 0;
    const raf = (time: number) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);

    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      lenisInstance = null;
    };
  }, []);
}
